import { useParams, Link } from 'react-router';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { ProgressBar, Button, Card } from '@lms/ui';
import {
  Clock,
  AlertCircle,
  ChevronRight,
  Loader2,
  Award,
  Info,
  FileText,
  Send,
  CheckCircle2,
  Shuffle,
  ArrowLeft,
  Users,
  TrendingUp,
} from 'lucide-react';
import { useEvaluation, useStartAttempt, useSubmitAttempt } from '../hooks/useEvaluations';
import { useAuthStore } from '../store/auth';
import type { EvaluationAttempt, EvaluationQuestion, EvaluationOption } from '@lms/shared/types';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { getGradePredikat } from '@lms/shared';
import { CurriculumPath, type CurriculumPathItem } from '../components/CurriculumPath';

export function EvaluationDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const { data: evaluation, isLoading: evalLoading, error } = useEvaluation(id || '', user?.id);
  const startAttemptMutation = useStartAttempt();
  const submitAttemptMutation = useSubmitAttempt();
  const { t, language } = useLanguage();

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [manualActiveAttempt, setManualActiveAttempt] = useState<EvaluationAttempt | null>(null);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);
  const [prevActiveAttemptId, setPrevActiveAttemptId] = useState<string | undefined>(undefined);

  // Contextual Navigation Resolution
  const programId = useMemo(() => {
    const ev = evaluation as unknown as Record<string, unknown> | undefined;
    if (!ev) return null;
    const stage = ev.stage as { level?: { programId?: string } } | undefined;
    const level = ev.level as { programId?: string } | undefined;
    const subject = ev.subject as { stage?: { level?: { programId?: string } } } | undefined;
    const session = ev.session as
      | { subject?: { stage?: { level?: { programId?: string } } } }
      | undefined;
    const mat = ev.material as { flow?: { programId?: string } } | undefined;
    const flow = ev.flow as { programId?: string } | undefined;

    return (
      (ev.programId as string | undefined) ||
      stage?.level?.programId ||
      level?.programId ||
      subject?.stage?.level?.programId ||
      session?.subject?.stage?.level?.programId ||
      mat?.flow?.programId ||
      flow?.programId ||
      null
    );
  }, [evaluation]);

  const programName = useMemo(() => {
    const ev = evaluation as unknown as Record<string, unknown> | undefined;
    if (!ev) return null;
    const prog = ev.program as { name?: string } | undefined;
    const stage = ev.stage as { level?: { program?: { name?: string } } } | undefined;
    const level = ev.level as { program?: { name?: string } } | undefined;
    const subject = ev.subject as
      | { stage?: { level?: { program?: { name?: string } } } }
      | undefined;
    const session = ev.session as
      | { subject?: { stage?: { level?: { program?: { name?: string } } } } }
      | undefined;

    return (
      prog?.name ||
      stage?.level?.program?.name ||
      level?.program?.name ||
      subject?.stage?.level?.program?.name ||
      session?.subject?.stage?.level?.program?.name ||
      null
    );
  }, [evaluation]);

  const stageInfo = useMemo(() => {
    const ev = evaluation as unknown as Record<string, unknown> | undefined;
    if (!ev) return null;
    const stage = ev.stage as { stageCode?: string; name?: string } | undefined;
    const level = ev.level as { name?: string } | undefined;
    const subject = ev.subject as { name?: string } | undefined;

    if (stage) {
      return `${stage.stageCode ? `Tahap ${stage.stageCode} • ` : ''}${stage.name || ''}`;
    }
    if (level) {
      return `Level • ${level.name || ''}`;
    }
    if (subject) {
      return `Mata Pelajaran • ${subject.name || ''}`;
    }
    return null;
  }, [evaluation]);

  const curriculumContext = useMemo(() => {
    const session = evaluation?.session ?? evaluation?.material?.session;
    const subject = evaluation?.subject ?? session?.subject;
    const stage = evaluation?.stage ?? subject?.stage;
    const level = evaluation?.level ?? stage?.level;
    return { level, stage, subject, session };
  }, [evaluation]);

  const curriculumAnchor = curriculumContext.session
    ? `#session-${curriculumContext.session.id}`
    : curriculumContext.subject
      ? `#subject-${curriculumContext.subject.id}`
      : curriculumContext.stage
        ? `#stage-${curriculumContext.stage.id}`
        : curriculumContext.level
          ? `#level-${curriculumContext.level.id}`
          : '';
  const returnUrl = programId ? `/programs/${programId}${curriculumAnchor}` : '/programs';

  // Derived state to compute unanswered questions count
  const unansweredCount = useMemo(() => {
    if (!evaluation?.questions) return 0;
    const answeredCount = Object.keys(answers).length;
    return evaluation.questions.length - answeredCount;
  }, [evaluation, answers]);

  // Derived state for pending attempts from server
  const pendingAttempt = useMemo(() => {
    return evaluation?.attempts?.find((a: EvaluationAttempt) => !a.submittedAt) || null;
  }, [evaluation]);

  // The actual active attempt is either one we just started/updated or a pending one from server
  const activeAttempt = manualActiveAttempt ?? pendingAttempt;

  // Reset auto-submit state when active attempt changes during render
  if (activeAttempt?.id !== prevActiveAttemptId) {
    setPrevActiveAttemptId(activeAttempt?.id);
    setHasAutoSubmitted(false);
  }

  const handleSubmit = useCallback(() => {
    if (!activeAttempt) return;
    const formattedAnswers = Object.entries(answers).map(([questionId, optionId]) => ({
      questionId,
      optionId,
    }));

    submitAttemptMutation.mutate(
      {
        attemptId: activeAttempt.id,
        answers: formattedAnswers,
      },
      {
        onSuccess: () => {
          setManualActiveAttempt(null);
          setAnswers({}); // Clear answers on success
        },
      }
    );
  }, [activeAttempt, answers, submitAttemptMutation]);

  const handleAutoSubmit = useCallback(() => {
    if (hasAutoSubmitted) return;
    setHasAutoSubmitted(true);
    handleSubmit();
  }, [handleSubmit, hasAutoSubmitted]);

  useEffect(() => {
    if (activeAttempt && evaluation?.timeLimit && !hasAutoSubmitted) {
      const start = new Date(activeAttempt.startedAt).getTime();
      const limit = evaluation.timeLimit * 60 * 1000;
      const end = start + limit;

      const interval = setInterval(() => {
        const now = new Date().getTime();
        const diff = end - now;
        if (diff <= 0) {
          clearInterval(interval);
          setTimeLeft(0);
          handleAutoSubmit();
        } else {
          setTimeLeft(Math.floor(diff / 1000));
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [activeAttempt, evaluation, handleAutoSubmit, hasAutoSubmitted]);

  const handleStart = () => {
    if (!user || !id) return;
    startAttemptMutation.mutate(
      { evaluationId: id, userId: user.id },
      {
        onSuccess: data => {
          setManualActiveAttempt(data as EvaluationAttempt);
          setAnswers({}); // Clear answers if retaking/starting
        },
      }
    );
  };

  const handleOptionChange = (questionId: string, optionId: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionId }));
  };

  if (evalLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
        <p className="text-sm font-body text-on-surface-variant tracking-wide">
          {t(copy.evaluationDetail.loading)}
        </p>
      </div>
    );
  }

  if (error || !evaluation) {
    return (
      <div className="bg-error-container text-on-error-container p-8 rounded-3xl animate-fadeIn max-w-2xl mx-auto my-12">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle />
          <h3 className="font-headline font-bold text-xl">{t(copy.evaluationDetail.errorTitle)}</h3>
        </div>
        <p className="text-sm font-body opacity-90">
          {(error as Error)?.message || t(copy.evaluationDetail.errorDesc)}
        </p>
        <Link
          to={returnUrl}
          className="inline-flex items-center gap-2 mt-6 text-sm font-bold underline"
        >
          <ArrowLeft size={16} />
          <span>{t(copy.evaluationDetail.backToCurriculum)}</span>
        </Link>
      </div>
    );
  }

  const isExam = evaluation.type === 'EXAM';
  const curriculumPathItems: CurriculumPathItem[] = [
    ...(programName
      ? [
          {
            key: 'program',
            label: t(copy.curriculum.program),
            value: programName,
            href: programId ? `/programs/${programId}` : '/programs',
          },
        ]
      : []),
    ...(curriculumContext.level
      ? [
          {
            key: 'level',
            label: t(copy.curriculum.level),
            value: curriculumContext.level.name,
            href: programId
              ? `/programs/${programId}#level-${curriculumContext.level.id}`
              : undefined,
          },
        ]
      : []),
    ...(curriculumContext.stage
      ? [
          {
            key: 'stage',
            label: t(copy.curriculum.stage),
            value: curriculumContext.stage.name,
            href: programId
              ? `/programs/${programId}#stage-${curriculumContext.stage.id}`
              : undefined,
          },
        ]
      : []),
    ...(curriculumContext.subject
      ? [
          {
            key: 'subject',
            label: t(copy.curriculum.subject),
            value: curriculumContext.subject.name,
            href: programId
              ? `/programs/${programId}#subject-${curriculumContext.subject.id}`
              : undefined,
          },
        ]
      : []),
    ...(curriculumContext.session
      ? [
          {
            key: 'session',
            label: t(copy.curriculum.session),
            value: `#${curriculumContext.session.sessionNumber} ${curriculumContext.session.title}`,
            href: returnUrl,
          },
        ]
      : []),
    {
      key: 'evaluation',
      label: t(copy.curriculum.evaluation),
      value: evaluation.title,
      current: true,
    },
  ];
  const completedAttempt = evaluation.attempts?.find((a: EvaluationAttempt) => !!a.submittedAt);

  if (completedAttempt && !activeAttempt) {
    const isPassed = !!completedAttempt.isPassed;
    const submittedAttemptsCount =
      evaluation.attempts?.filter((a: EvaluationAttempt) => !!a.submittedAt).length || 0;
    const isAttemptsExhausted =
      evaluation.maxAttempts > 0 && submittedAttemptsCount >= evaluation.maxAttempts;

    return (
      <div className="max-w-2xl mx-auto space-y-8 animate-fadeIn py-12">
        {/* Top Program Context Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-outline-variant/10">
          <Link
            to={returnUrl}
            className="inline-flex items-center gap-2 text-xs font-bold text-on-surface-variant hover:text-primary transition-colors no-underline group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            <span>{t(copy.evaluationDetail.backToCurriculum)}</span>
          </Link>
          {programName && (
            <span className="text-[11px] font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-full">
              {programName} {stageInfo ? `• ${stageInfo}` : ''}
            </span>
          )}
        </div>

        <CurriculumPath
          ariaLabel={t(copy.curriculum.ariaLabel)}
          eyebrow={t(copy.curriculum.currentPosition)}
          items={curriculumPathItems}
        />

        <div className="text-center space-y-4">
          <div
            className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 shadow-level2 ${
              isPassed ? 'bg-success text-on-success' : 'bg-error-container text-on-error-container'
            }`}
          >
            {isPassed ? <Award size={48} /> : <AlertCircle size={48} />}
          </div>
          <h1 className="text-4xl font-headline font-bold text-on-surface tracking-tight">
            {isPassed ? t(copy.evaluationDetail.passedTitle) : t(copy.evaluationDetail.failedTitle)}
          </h1>
          <p className="text-on-surface-variant max-w-sm mx-auto">
            {isPassed ? t(copy.evaluationDetail.passedDesc) : t(copy.evaluationDetail.failedDesc)}
          </p>
        </div>

        {isAttemptsExhausted && !isPassed && (
          <div className="flex items-start gap-3 p-5 bg-error-container/10 border border-error/20 text-error rounded-3xl w-full">
            <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
            <div className="text-left">
              <p className="text-sm font-bold font-headline mb-1">
                {t(copy.evaluationDetail.attemptLimitReached)}
              </p>
              <p className="text-xs text-on-surface-variant leading-relaxed">
                {t(copy.evaluationDetail.attemptLimitReachedDesc).replace(
                  '{max}',
                  String(evaluation.maxAttempts)
                )}
              </p>
            </div>
          </div>
        )}

        <Card className="p-8 space-y-6 bg-surface-container-lowest border-on-surface/5">
          <div className="flex justify-between items-end">
            <div>
              <p className="text-[10px] font-label text-on-surface-variant uppercase tracking-widest font-bold mb-1">
                {t(copy.evaluationDetail.performance)}
              </p>
              <div className="flex items-center gap-3">
                <span
                  className={`text-5xl font-headline font-bold ${isPassed ? 'text-success' : 'text-error'}`}
                >
                  {Math.round(completedAttempt.score || 0)}%
                </span>
                <div
                  className={`px-2 py-1 rounded border text-xs font-bold uppercase tracking-widest ${
                    (completedAttempt.score || 0) >= 80
                      ? 'bg-primary/10 text-primary border-primary/20'
                      : (completedAttempt.score || 0) >= 60
                        ? 'bg-secondary/10 text-secondary border-secondary/20'
                        : 'bg-error/10 text-error border-error/20'
                  }`}
                >
                  {language === 'ar'
                    ? getGradePredikat(completedAttempt.score || 0).arabic
                    : getGradePredikat(completedAttempt.score || 0).indonesian}
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-label text-on-surface-variant uppercase tracking-widest font-bold mb-1">
                {t(copy.evaluationDetail.passingScore)}
              </p>
              <span className="text-xl font-headline font-bold text-on-surface">
                {evaluation.passingScore}%
              </span>
            </div>
          </div>
          <ProgressBar
            value={Math.round(completedAttempt.score || 0)}
            variant={isPassed ? 'success' : 'primary'}
          />

          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-on-surface/5">
            <div className="p-4 bg-surface-container rounded-2xl">
              <p className="text-[10px] uppercase text-on-surface-variant font-bold mb-1">
                {t(copy.evaluationDetail.submitted)}
              </p>
              <p className="text-sm font-body">
                {completedAttempt.submittedAt
                  ? new Date(completedAttempt.submittedAt).toLocaleString()
                  : '---'}
              </p>
            </div>
            <div className="p-4 bg-surface-container rounded-2xl">
              <p className="text-[10px] uppercase text-on-surface-variant font-bold mb-1">
                {t(copy.evaluationDetail.timeUsed)}
              </p>
              <p className="text-sm font-body">
                {completedAttempt.timeSpent ? Math.floor(completedAttempt.timeSpent / 60) : 0}{' '}
                {t(copy.evaluationDetail.minutes)}
              </p>
            </div>
          </div>
        </Card>

        <div className="flex flex-col gap-3">
          {isExam && isPassed && programId ? (
            <Link
              to={`/programs/${programId}/reflection`}
              className="bg-primary text-on-primary w-full h-14 rounded-2xl text-base font-bold shadow-level1 flex items-center justify-center no-underline hover:bg-primary-container active:scale-95 transition-all"
            >
              {t(copy.evaluationDetail.proceedReflection)}
            </Link>
          ) : (
            <Link
              to={returnUrl}
              className="bg-primary text-on-primary w-full h-14 rounded-2xl text-base font-bold shadow-level1 flex items-center justify-center no-underline hover:bg-primary-container active:scale-95 transition-all"
            >
              {isPassed
                ? t(copy.evaluationDetail.continueCurriculum)
                : t(copy.evaluationDetail.backToCurriculum)}
            </Link>
          )}
          {!isPassed && !isAttemptsExhausted && (
            <Button variant="secondary" className="w-full h-14 rounded-2xl" onClick={handleStart}>
              {t(copy.evaluationDetail.retakeBtn)}
            </Button>
          )}
        </div>

        {evaluation.showCorrectAnswers &&
          evaluation.questions &&
          evaluation.questions.length > 0 && (
            <div className="mt-12 space-y-8 pt-12 border-t border-on-surface/10">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-headline font-bold text-on-surface">
                  {t(copy.evaluationDetail.title)} - {t(copy.evaluationDetail.correctAnswer)}
                </h2>
              </div>
              <div className="space-y-10">
                {(evaluation.questions || [])
                  .sort((a: EvaluationQuestion, b: EvaluationQuestion) => a.order - b.order)
                  .map((q: EvaluationQuestion, idx: number) => {
                    const userAnswer = completedAttempt.answers?.find(
                      ans => ans.questionId === q.id
                    );
                    const selectedOptionId = userAnswer?.optionId;
                    const isCorrect = userAnswer?.isCorrect;

                    return (
                      <div key={q.id} className="space-y-4">
                        <div className="flex gap-4 items-start">
                          <span
                            className={`w-10 h-10 rounded-xl flex items-center justify-center font-headline font-bold text-base flex-shrink-0 ${isCorrect ? 'bg-success/10 text-success' : 'bg-error/10 text-error'}`}
                          >
                            {idx + 1}
                          </span>
                          <div className="flex-1 space-y-4">
                            <h3 className="text-lg font-headline font-medium text-on-surface leading-snug">
                              {q.question}
                            </h3>
                            <div className="space-y-3">
                              {(q.options || [])
                                .sort(
                                  (a: EvaluationOption, b: EvaluationOption) => a.order - b.order
                                )
                                .map((opt: EvaluationOption) => {
                                  const isSelected = selectedOptionId === opt.id;
                                  const isOptionCorrect = opt.isCorrect;

                                  let optionClass =
                                    'bg-surface-container-lowest border-on-surface/5';
                                  if (isSelected && isOptionCorrect) {
                                    optionClass =
                                      'bg-success/10 border-success/30 text-success font-medium';
                                  } else if (isSelected && !isOptionCorrect) {
                                    optionClass =
                                      'bg-error/10 border-error/30 text-error font-medium';
                                  } else if (isOptionCorrect) {
                                    optionClass =
                                      'bg-success/5 border-success border-dashed text-success';
                                  }

                                  return (
                                    <div
                                      key={opt.id}
                                      className={`p-4 rounded-2xl border-2 transition-all flex items-center gap-4 ${optionClass}`}
                                    >
                                      <div
                                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? (isOptionCorrect ? 'border-success bg-success' : 'border-error bg-error') : isOptionCorrect ? 'border-success' : 'border-on-surface-variant'}`}
                                      >
                                        {isSelected && (
                                          <div className="w-2 h-2 bg-white rounded-full" />
                                        )}
                                      </div>
                                      <span className="text-base font-body">{opt.text}</span>
                                      {isOptionCorrect && (
                                        <span className="ml-auto text-[10px] uppercase font-bold text-success tracking-widest bg-success/20 px-2.5 py-1 rounded-md">
                                          {t(copy.evaluationDetail.correctAnswer)}
                                        </span>
                                      )}
                                      {isSelected && !isOptionCorrect && (
                                        <span className="ml-auto text-[10px] uppercase font-bold text-error tracking-widest bg-error/20 px-2.5 py-1 rounded-md">
                                          {t(copy.evaluationDetail.yourAnswer)}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                            {q.explanation && (
                              <div className="mt-4 p-4 bg-secondary/10 rounded-2xl border border-secondary/20 text-sm font-body text-on-surface-variant flex gap-3">
                                <Info className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                                <div>
                                  <strong className="text-secondary block mb-1 uppercase text-xs tracking-widest font-bold">
                                    {t(copy.evaluationDetail.explanation)}
                                  </strong>
                                  {q.explanation}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
      </div>
    );
  }

  if (activeAttempt) {
    const formatTime = (seconds: number) => {
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${m}:${s.toString().padStart(2, '0')}`;
    };

    return (
      <div
        className={`min-h-screen -mt-24 pt-24 pb-20 ${isExam ? 'bg-surface-container-lowest' : ''}`}
      >
        <div className="max-w-3xl mx-auto px-4 space-y-8 animate-slideInUp">
          <div className="sticky top-20 z-20 py-4 bg-surface-container-lowest/80 backdrop-blur-xl border-b border-on-surface/5 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-headline font-bold text-on-surface">
                {evaluation.title}
              </h2>
              <div className="flex items-center gap-2 text-warning">
                <Clock size={16} />
                <span className="text-xs font-mono font-bold">
                  {timeLeft !== null ? formatTime(timeLeft) : '---'}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-12 py-8">
            {(evaluation.questions || [])
              .sort((a: EvaluationQuestion, b: EvaluationQuestion) => a.order - b.order)
              .map((q: EvaluationQuestion, idx: number) => (
                <div key={q.id} className="space-y-6">
                  <div className="flex gap-4 items-start">
                    <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
                      {idx + 1}
                    </span>
                    <h3 className="text-xl font-headline font-medium text-on-surface leading-snug">
                      {q.question}
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 gap-3 pl-12">
                    {(q.options || [])
                      .sort((a: EvaluationOption, b: EvaluationOption) => a.order - b.order)
                      .map((opt: EvaluationOption) => (
                        <label
                          key={opt.id}
                          className={`flex items-center gap-4 p-5 rounded-2xl border-2 transition-all cursor-pointer group ${
                            answers[q.id] === opt.id
                              ? 'bg-primary/5 border-primary shadow-level1'
                              : 'bg-surface-container-lowest border-on-surface/5 hover:border-primary/30'
                          }`}
                        >
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                              answers[q.id] === opt.id
                                ? 'bg-primary border-primary'
                                : 'border-on-surface-variant'
                            }`}
                          >
                            {answers[q.id] === opt.id && (
                              <div className="w-2 h-2 bg-on-primary rounded-full" />
                            )}
                          </div>
                          <input
                            type="radio"
                            className="hidden"
                            name={q.id}
                            checked={answers[q.id] === opt.id}
                            onChange={() => handleOptionChange(q.id, opt.id)}
                          />
                          <span className="text-base font-body text-on-surface">{opt.text}</span>
                        </label>
                      ))}
                  </div>
                </div>
              ))}

            {/* Bottom Submit Actions & Feedback */}
            <div className="pt-8 border-t border-on-surface/10 flex flex-col items-center gap-4">
              {unansweredCount > 0 ? (
                <div className="flex items-center gap-3 p-4 bg-error-container/20 border border-error/20 text-error rounded-2xl w-full">
                  <AlertCircle size={18} className="flex-shrink-0" />
                  <p className="text-sm font-medium font-headline">
                    {t(copy.evaluationDetail.unansweredWarning).replace(
                      '{count}',
                      String(unansweredCount)
                    )}
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-4 bg-success/5 border border-success/20 text-success rounded-2xl w-full animate-fadeIn">
                  <CheckCircle2 size={18} className="flex-shrink-0" />
                  <p className="text-sm font-medium font-headline">
                    {t(copy.evaluationDetail.allAnsweredFeedback)}
                  </p>
                </div>
              )}
              <Button
                onClick={() => setIsConfirmOpen(true)}
                disabled={submitAttemptMutation.isPending || unansweredCount > 0}
                className="w-full h-14 rounded-2xl text-base font-bold shadow-level2 gap-2"
              >
                {submitAttemptMutation.isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <Send size={18} />
                )}
                {t(copy.evaluationDetail.finishBtn)}
              </Button>
            </div>
          </div>
        </div>

        {/* Recheck Confirmation Dialog */}
        {isConfirmOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
            <div className="bg-surface-container-lowest border border-on-surface/5 rounded-3xl p-6 max-w-md w-full shadow-level4 space-y-6 animate-scaleIn">
              <div className="flex gap-4 items-start">
                <div className="p-3 bg-primary/10 text-primary rounded-2xl">
                  <Info size={24} />
                </div>
                <div className="space-y-2 flex-1">
                  <h3 className="text-xl font-headline font-bold text-on-surface">
                    {t(copy.evaluationDetail.recheckConfirmTitle)}
                  </h3>
                  <p className="text-sm font-body text-on-surface-variant leading-relaxed">
                    {t(copy.evaluationDetail.recheckConfirmDesc)}
                  </p>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="secondary"
                  className="rounded-xl px-5"
                  onClick={() => setIsConfirmOpen(false)}
                >
                  {t(copy.evaluationDetail.recheckConfirmCancel)}
                </Button>
                <Button
                  className="rounded-xl px-5"
                  onClick={() => {
                    setIsConfirmOpen(false);
                    handleSubmit();
                  }}
                >
                  {t(copy.evaluationDetail.recheckConfirmSubmit)}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-fadeIn py-12">
      {/* Top Program Context Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-outline-variant/10">
        <Link
          to={returnUrl}
          className="inline-flex items-center gap-2 text-xs font-bold text-on-surface-variant hover:text-primary transition-colors no-underline group"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          <span>{t(copy.evaluationDetail.backToCurriculum)}</span>
        </Link>
        {programName && (
          <span className="text-[11px] font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-full">
            {programName} {stageInfo ? `• ${stageInfo}` : ''}
          </span>
        )}
      </div>

      <CurriculumPath
        ariaLabel={t(copy.curriculum.ariaLabel)}
        eyebrow={t(copy.curriculum.currentPosition)}
        items={curriculumPathItems}
      />

      <div className="text-center space-y-4">
        <div className="w-20 h-20 bg-primary/10 rounded-3xl flex items-center justify-center mx-auto mb-6 text-primary shadow-level1 rotate-3 hover:rotate-0 transition-transform">
          <FileText size={40} />
        </div>
        <h1 className="text-4xl font-headline font-bold text-on-surface tracking-tight leading-tight">
          {evaluation.title}
        </h1>
        <p className="text-on-surface-variant leading-relaxed max-w-md mx-auto">
          {evaluation.description || t(copy.evaluationDetail.defaultDesc)}
        </p>
      </div>

      {/* Informative Stats & Passing Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Users size={20} />
          </div>
          <div>
            <p className="text-[10px] uppercase font-bold text-on-surface-variant/70">
              Telah Mengerjakan
            </p>
            <p className="text-base font-bold text-on-surface font-headline">
              {(
                (evaluation as unknown as Record<string, unknown>)?.stats as {
                  totalSubmissions?: number;
                }
              )?.totalSubmissions ?? 0}{' '}
              Peserta
            </p>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-success/10 text-success flex items-center justify-center shrink-0">
            <TrendingUp size={20} />
          </div>
          <div>
            <p className="text-[10px] uppercase font-bold text-on-surface-variant/70">
              Tingkat Kelulusan
            </p>
            <p className="text-base font-bold text-success font-headline">
              {((evaluation as unknown as Record<string, unknown>)?.stats as { passRate?: number })
                ?.passRate ?? 100}
              % Lulus
            </p>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center shrink-0">
            <Award size={20} />
          </div>
          <div>
            <p className="text-[10px] uppercase font-bold text-on-surface-variant/70">
              Standar KKM
            </p>
            <p className="text-base font-bold text-secondary font-headline">
              {evaluation.passingScore}% Skor
            </p>
          </div>
        </div>
      </div>

      <Card className="p-8 bg-surface-container-lowest border-on-surface/5">
        <h3 className="text-xs font-label text-on-surface-variant uppercase tracking-[0.2em] font-bold mb-6">
          {t(copy.evaluationDetail.rules)}
        </h3>
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/5 rounded-2xl text-primary">
              <Clock size={24} />
            </div>
            <div>
              <p className="text-sm font-bold text-on-surface">
                {t(copy.evaluationDetail.timeLimit)}
              </p>
              <p className="text-xs text-on-surface-variant">
                {evaluation.timeLimit
                  ? `${evaluation.timeLimit} ${t(copy.evaluations.minutes)}`
                  : t(copy.evaluations.noLimit)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-secondary/5 rounded-2xl text-secondary">
              <Award size={24} />
            </div>
            <div>
              <p className="text-sm font-bold text-on-surface">
                {t(copy.evaluationDetail.passingCriteria)}
              </p>
              <p className="text-xs text-on-surface-variant">
                {t(copy.evaluationDetail.passingCriteriaDesc).replace(
                  '{score}',
                  String(evaluation.passingScore)
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-surface-container rounded-2xl text-on-surface-variant">
              <Info size={24} />
            </div>
            <div>
              <p className="text-sm font-bold text-on-surface">
                {t(copy.evaluationDetail.attemptLimit)}
              </p>
              <p className="text-xs text-on-surface-variant">
                {t(copy.evaluationDetail.attemptLimitDesc).replace(
                  '{max}',
                  String(evaluation.maxAttempts)
                )}
              </p>
            </div>
          </div>
          {evaluation.numberOfQuestions &&
            evaluation.bankSize &&
            evaluation.bankSize > evaluation.numberOfQuestions && (
              <div className="flex items-center gap-4">
                <div className="p-3 bg-tertiary/5 rounded-2xl text-tertiary">
                  <Shuffle size={24} />
                </div>
                <div>
                  <p className="text-sm font-bold text-on-surface">
                    {t(copy.evaluationDetail.questionPool)}
                  </p>
                  <p className="text-xs text-on-surface-variant">
                    {t(copy.evaluationDetail.questionPoolDesc)
                      .replace('{sample}', String(evaluation.numberOfQuestions))
                      .replace('{total}', String(evaluation.bankSize))}
                  </p>
                </div>
              </div>
            )}
        </div>
      </Card>

      <div className="bg-primary/5 rounded-2xl p-4 border border-dashed border-primary/20 flex items-start gap-3">
        <AlertCircle size={18} className="text-primary flex-shrink-0 mt-0.5" />
        <p className="text-xs text-primary font-medium leading-relaxed">
          {t(copy.evaluationDetail.ackStart)} {isExam ? t(copy.evaluationDetail.ackExam) : ''}
        </p>
      </div>

      <Button
        onClick={handleStart}
        disabled={startAttemptMutation.isPending}
        className="w-full h-16 rounded-2xl text-lg font-bold shadow-level2 group"
      >
        {startAttemptMutation.isPending ? (
          <Loader2 className="animate-spin" />
        ) : (
          <>
            {t(copy.evaluationDetail.startBtn)}
            <ChevronRight className="group-hover:translate-x-1 transition-transform" />
          </>
        )}
      </Button>
    </div>
  );
}
